import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpUtilService } from '@services/http-util.service';
import { EnvironmentService } from '@services/environment.service';
import {
  AuthroizationResponse,
  BlackbaudAuthService,
  ExchangeResponse,
} from '@app/admin/services/blackbaud-auth.service';
import { NotificationsService } from '@services/notifications.service';

@Component({
  selector: 'app-auth-landing',
  templateUrl: './auth-landing.component.html',
  styleUrl: './auth-landing.component.scss',
  standalone: false,
})
export class AuthLandingComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpUtilService,
    private environmentService: EnvironmentService,
    private blackbaudAuthService: BlackbaudAuthService,
    private notifications: NotificationsService
  ) {}

  count = 0;

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['x_state']) {
        this.authBrightspace(params);
      } else if (params['code']) {
        this.authBlackbaud(params);
      } else {
        const errorMessage =
          params['error_message'] ||
          params['x_error'] ||
          'Error authenticating integration';
        this.handleError(errorMessage, '/admin/integrations');
      }
    });
  }

  /**
   * Authenticate with Brightspace
   * @param params - The parameters from the URL
   */
  authBrightspace(params) {
    try {
      const state = atob(params['x_state']);
      let integrationDetails = JSON.parse(state);
      if (!params['x_a'] || !params['x_b']) {
        this.handleError(
          'Error authenticating integration',
          '/admin/integrations'
        );
        return;
      }
      localStorage.setItem('brightspace_auth', JSON.stringify(params));
      this.http
        .request(
          'PUT',
          `${this.environmentService.environment.apiRoot}/integrations/lms/${integrationDetails.name}`,
          {
            json_data: {
              userId: params['x_a'],
              userKey: params['x_b'],
            },
            id: integrationDetails.id,
          },
          null,
          true
        )
        .subscribe(response => {
          this.router.navigate([integrationDetails.currentRoute]);
        });
    } catch (e) {
      this.router.navigate(['/admin/integrations']);
      this.notifications.popNotification(
        'Error Authenticating Integration',
        ''
      );
      return;
    }
  }

  /**
   * Authenticate with Blackbaud for Raser's Edge
   * @param params - The parameters from the URL
   */
  authBlackbaud(params) {
    const error = params['error'] || null;
    let details;
    try {
      details = this.blackbaudAuthService.getStateDetails(params['state']);
    } catch (error) {
      this.blackbaudAuthService.removeState(params['state']);
      this.handleError("Error authorizing Raser's Edge", '/admin/integrations');
      return;
    }
    if (!details) {
      this.blackbaudAuthService.removeState(params['state']);
      this.handleError("Error authorizing Raser's Edge", '/admin/integrations');
      return;
    }

    if (error) {
      const errorMessage = params['error_message'];
      this.blackbaudAuthService.removeState(params['state']);
      this.handleError(errorMessage, details.currentRoute);
      return;
    }
    const targetRoute = details.currentRoute;
    const integrationDetails = {
      id: details.id,
      name: details.name,
      integration_type: 'crm',
    };
    const authorizationResponse: AuthroizationResponse = {
      code: params['code'],
      state: params['state'],
    };
    if (this.count < 1) {
      this.count++;
      this.blackbaudAuthService
        .exchangeAuthorizationCode(authorizationResponse)
        .subscribe({
          next: (exchangeResponse: ExchangeResponse) => {
            const data = {
              integration_type: 'crm',
              json_data: {
                accessToken: exchangeResponse.access_token,
                state: exchangeResponse.state,
                refresh_token: exchangeResponse.refresh_token,
                token_created: new Date().toISOString(),
                token_environment_name: exchangeResponse.environment_name,
                token_legal_entity_name: exchangeResponse.legal_entity_name,
                token_environment_id: exchangeResponse.environment_id,
                token_legal_entity_id: exchangeResponse.legal_entity_id,
                token_user_id: exchangeResponse.user_id,
                token_mode: exchangeResponse.mode,
                token_scope: exchangeResponse.scope,
              },
            };
            this.updateIntegration(integrationDetails, data).subscribe({
              next: () => {
                this.router.navigate([targetRoute]);
              },
              error: error => {
                this.handleError('Error updating integration', targetRoute);
                return;
              },
            });
          },
          error: error => {
            this.handleError(
              'Error exchanging authorization code',
              targetRoute
            );
            this.blackbaudAuthService.removeState(params['state']);
            return;
          },
        });
    } else {
      console.log('over count');
    }
  }

  /**
   * Update the integration to store any auth details form the target system
   * @param integrationDetails - The integration details
   * @param data - The data to update the integration with
   * @returns The updated integration
   */

  updateIntegration(integrationDetails, data) {
    return this.http.request(
      'PUT',
      `${this.environmentService.environment.apiRoot}/integrations/${integrationDetails.integration_type}/${integrationDetails.name}`,
      {
        json_data: data.json_data,
        id: integrationDetails.id,
      },
      null,
      true
    );
  }

  /**
   * Handle an error which will take the user back to the previous page or root page
   * @param errorMessage - The error message
   * @param redirect - The redirect URL
   */
  handleError(errorMessage, redirect = '/') {
    this.notifications.popNotification('', errorMessage, null, {
      status: 'danger',
    });
    this.router.navigate([redirect]);
  }
}
